import React from "react";
import {
    Box,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    TabsProps,
} from "@chakra-ui/react";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

export type ReusableStyledTabItem = {
    key: string;
    label: React.ReactNode;
    content?: React.ReactNode;
    isDisabled?: boolean;
};

type ReusableStyledTabsProps = {
    tabs: ReusableStyledTabItem[];
    index?: number;
    defaultIndex?: number;
    onChange?: (index: number) => void;
    isLazy?: boolean;
    width?: string;
    showPanels?: boolean;
    panelsMt?: number | string;
    tabsProps?: Omit<TabsProps, "children" | "index" | "defaultIndex" | "onChange">;
};

const ReusableStyledTabs: React.FC<ReusableStyledTabsProps> = ({
    tabs,
    index,
    defaultIndex,
    onChange,
    isLazy = true,
    width = "full",
    showPanels = true,
    panelsMt = 4,
    tabsProps,
}) => {
    const { theme } = useCustomTheme();

    return (
        <Tabs
            variant="unstyled"
            index={index}
            defaultIndex={defaultIndex}
            onChange={onChange}
            isLazy={isLazy}
            width={width}
            {...tabsProps}
        >
            <Box
                w="100%"
                overflowX="auto"
                overflowY="hidden"
                bg={theme.surfaceAlt}
                border="1px solid"
                borderColor={theme.border}
                borderRadius="8px"
            >
                <TabList
                    gap={1}
                    minW="max-content"
                    w="100%"
                    borderBottom="1px solid"
                    borderColor={theme.border}
                >
                    {tabs.map((tab) => (
                        <Tab
                            key={tab.key}
                            isDisabled={tab.isDisabled}
                            position="relative"
                            px={5}
                            py={3}
                            fontSize={{ base: "sm", md: "md" }}
                            fontWeight="700"
                            color={theme.textMuted}
                            bg="transparent"
                            borderTopRadius="md"
                            borderBottom="2px solid transparent"
                            _hover={{
                                color: theme.text,
                                bg: theme.hover,
                            }}
                            _selected={{
                                color: theme.text,
                                bg: theme.surface,
                                borderBottomColor: theme.inputBorderFocus,
                                boxShadow: theme.shadowSm,
                            }}
                            transition="all 0.2s ease"
                            whiteSpace="nowrap"
                        >
                            {tab.label}
                        </Tab>
                    ))}
                </TabList>
            </Box>

            {showPanels && (
                <TabPanels mt={panelsMt}>
                    {tabs.map((tab) => (
                        <TabPanel key={tab.key} px={0} py={0}>
                            {tab.content}
                        </TabPanel>
                    ))}
                </TabPanels>
            )}
        </Tabs>
    );
};

export default ReusableStyledTabs;